/**
 * SubmoduleRow - Draggable submodule component for reorganization
 * Constrained to move only within parent module
 */

import type { ModuleItemType, SubmoduleItem } from 'app/types/modules';
import AssetImage from '../../../../atoms/AssetImage/AssetImage';
import Icon from '../../../../atoms/Icon/Icon';
import { stripHtml } from 'core/utils/utils';
import React from 'react';
import CreatedByInfo from './CreatedByInfo';
import ItemActions from './itemActions';
import { useSubmoduleRow } from './useSubmoduleRow';

type SubmoduleRowProps = {
  submodule: SubmoduleItem;
  isDragOverlay?: boolean;
  reorderMode: boolean;
  onDelete?: (item: ModuleItemType | SubmoduleItem) => void;
  onEdit?: (item: ModuleItemType | SubmoduleItem) => void;
  onToggleVisibility?: (item: ModuleItemType | SubmoduleItem) => void;
  onOpenPhoto?: (item: ModuleItemType | SubmoduleItem) => void;
};

function SubmoduleRow({ submodule, isDragOverlay = false, reorderMode, onDelete, onEdit, onToggleVisibility, onOpenPhoto }: SubmoduleRowProps): JSX.Element {
  const {
    rootRef,
    rootStyle,
    rootClassName,
    rootDragProps,
    dragHandleClassName,
    imageClassName,
    imageInteractiveProps,
    assetImageId,
    submoduleName,
    shouldRenderItemActions,
    shouldRenderCreatedByInfo,
  } = useSubmoduleRow({ submodule, isDragOverlay, reorderMode, onOpenPhoto });

  return (
    <div ref={rootRef} style={rootStyle} className={rootClassName} {...rootDragProps}>
      <div className={dragHandleClassName}>
        <Icon name="drag-bullets" size="medium" color="grey-03" />
      </div>
      <div className={imageClassName} {...imageInteractiveProps}>
        <AssetImage src={submodule?.image} id={assetImageId} alt={submoduleName} />
      </div>
      <div className="submodule-row__info">
        <span className="submodule-row__name">{stripHtml(submoduleName)}</span>
      </div>
      {shouldRenderItemActions && <ItemActions item={submodule} onDelete={onDelete} onEdit={onEdit} onToggleVisibility={onToggleVisibility} />}
      {shouldRenderCreatedByInfo && (
        <div className="module-row__created-by">
          <CreatedByInfo avatar={submodule.created_by?.avatar} name={submodule.created_by?.name} role={submodule.created_by?.role_guid} />
        </div>
      )}
    </div>
  );
}

export default SubmoduleRow;
